import { CLASSIFICATION_MARKET_TARGET_ATTR_NAME } from '../constants';
import type { DynamicFieldItem } from '../types/weeklyReport';
import type { WeeklyReportPublicSettingRow } from './weeklyReportPublicScopeDisplay';
import {
  buildAccessSettingFromWrPublicSetting,
  canAccessPartialRowByScope,
  normalizeScopeCode,
  parseWrPublicSettingOptionIds,
} from './weeklyReportOrganizationAccess';
import {
  mergeAccessSettingByUnion,
  type ReportTypeAccessSetting,
} from '../components/WeeklyReport/SearchArea/reportTypeAccess';

const positiveId = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : null;
};

// 未選択の空要素（'' / null / 値が空のオブジェクト）は「選択なし」として除外する。
const isBlankSelection = (selected: unknown): boolean => {
  if (selected == null) return true;
  if (typeof selected === 'object') {
    const inner = (selected as any).value ?? (selected as any).id ?? (selected as any).label ?? (selected as any).name;
    return String(inner ?? '').trim() === '';
  }
  return String(selected).trim() === '';
};

const selectedValues = (value: unknown): unknown[] =>
  (Array.isArray(value) ? value : [value]).filter((selected) => !isBlankSelection(selected));

export type SelectedWeeklyReportOption = {
  optionId: string;
  label: string;
};

export const getSelectedWeeklyReportOptions = (
  fields: DynamicFieldItem[]
): SelectedWeeklyReportOption[] => {
  const optionsById = new Map<string, SelectedWeeklyReportOption>();
  fields
    .filter((field) => field?.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME)
    .forEach((field) => {
      selectedValues(field.value).forEach((selected) => {
        const rawValue =
          selected && typeof selected === 'object'
            ? (selected as any).value ?? (selected as any).id
            : selected;
        const rawLabel =
          selected && typeof selected === 'object'
            ? (selected as any).label ?? (selected as any).name
            : selected;
        const directId = positiveId(rawValue);
        const option = (field.options ?? []).find(
          (candidate) =>
            String(candidate.value ?? '').trim() === String(rawValue ?? '').trim() ||
            String(candidate.label ?? '').trim() === String(rawLabel ?? '').trim()
        );
        const optionId = directId ?? positiveId(option?.value);
        if (!optionId) return;

        const selectedLabel =
          selected && typeof selected === 'object'
            ? String((selected as any).label ?? (selected as any).name ?? '').trim()
            : '';
        const optionLabel = String(option?.label ?? '').trim();
        const rawTextLabel = directId ? '' : String(rawLabel ?? '').trim();
        const label = selectedLabel || optionLabel || rawTextLabel || optionId;
        const current = optionsById.get(optionId);
        if (!current || current.label === optionId) {
          optionsById.set(optionId, { optionId, label });
        }
      });
    });
  return Array.from(optionsById.values());
};

export const getSelectedWeeklyReportOptionIds = (
  fields: DynamicFieldItem[]
): string[] => getSelectedWeeklyReportOptions(fields).map(({ optionId }) => optionId);

export const shouldWarnBeforeWeeklyReportPublication = (args: {
  fields: DynamicFieldItem[];
  settings: WeeklyReportPublicSettingRow[];
  sourceDepartmentId: unknown;
  sourceTeamId?: unknown;
  sourceDepartmentCode?: string;
}): boolean => {
  const classificationFields = args.fields.filter(
    (field) =>
      field?.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME &&
      selectedValues(field.value).length > 0
  );
  if (classificationFields.length === 0) return false;

  const selectedOptionIds = getSelectedWeeklyReportOptionIds(args.fields);
  // 選択済みなのにIDへ解決できない場合は、誤って無警告で発行しない。
  if (selectedOptionIds.length === 0) return true;

  const sourceDepartmentId = positiveId(args.sourceDepartmentId);
  if (!sourceDepartmentId) return true;
  const sourceTeamId = positiveId(args.sourceTeamId);

  const knownOptionIds = new Set(
    args.fields.flatMap((field) =>
      field.type === CLASSIFICATION_MARKET_TARGET_ATTR_NAME
        ? (field.options ?? []).map((option) => String(option.value))
        : []
    )
  );
  selectedOptionIds.forEach((optionId) => knownOptionIds.add(optionId));
  let access: ReportTypeAccessSetting = {
    visibility: 'private',
    departments: [],
    teams: [],
  };

  // 検索画面と同じ合算処理を使う。private と限定公開が混在しても限定公開を維持する。
  for (const optionId of selectedOptionIds) {
    const matchingSettings = args.settings.filter(
      (row) => parseWrPublicSettingOptionIds(
        row.option_id ?? row.optionId ?? row.id,
        knownOptionIds
      ).includes(optionId)
    );

    // 公開設定がない分類は、既存仕様の公開フォールバックを維持する。
    if (matchingSettings.length === 0) return false;
    for (const row of matchingSettings) {
      access = mergeAccessSettingByUnion(
        access,
        buildAccessSettingFromWrPublicSetting(row)
      );
    }
  }

  if (access.visibility === 'public' || access.visibility === 'private') return false;
  const hasScope = [...access.departments, ...access.teams].some(
    (scope) => normalizeScopeCode(scope).length > 0
  );
  // 限定公開の対象が空なら既存閲覧判定は発行元の課へフォールバックする。
  if (!hasScope) return false;

  return !canAccessPartialRowByScope(access, sourceDepartmentId, {
    userDeptIdRaw: sourceDepartmentId,
    userTeamIdRaw: sourceTeamId,
    deptIdToLabelMap: args.sourceDepartmentCode
      ? { [sourceDepartmentId]: args.sourceDepartmentCode }
      : undefined,
  });
};
